import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import {
  ADDITIONAL_ATTRIBUTE_FIELDS,
  ATTRIBUTE_TYPE_TEXT,
  bulkUploadRecordsWidth,
  fullNameWidth,
  handleValidateCodevalue,
  STATIC_ENTITY_CONFIG,
  whocolumnWidth,
  workflowNameWidth,
  dateTimeISTFormatter,
} from "../Utility/constant";
import { useDispatch, useSelector } from "react-redux";
import {
  createRequestData,
  downloadDocument,
  getRequestData,
} from "../Utility/apiHelper";
import { useSnackbar } from "notistack";
import BiztantuLoader from "./Loader";
import {
  CreateLeadFooter,
  StyledFlexStart,
  StyledLeadContainer,
  StyledSubHeader,
} from "./Styled";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import DataGridTable from "./DataGrid";
import DownloadIcon from "@mui/icons-material/Download";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { requestHideLoader, requestShowLoader } from "../Store/Global/action";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "./Widgets/InfoIcon";
import useNavigation from "../Utility/hooks/useNavigation";

const CommonBulkUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dataGridData, setDataGridData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const internationalise = useSelector(
    (state) => state?.internationalise?.currentHashMapData,
  );
  const ownerlist = useSelector((state) => state?.owner?.ownerlist);
  const fileInputRef = useRef(null);
  const location = useLocation();
    const { goBackTo } = useNavigation();
  const {
    customizationData,
    from,
    title,
    selectedEntity = "",
    searchEntity,
    CUSTOMIZABLE_FIELDS,
    removeFields = ["selectedOwner"],
  } = location.state || {};
  
  const goBack = () => goBackTo(from,searchEntity);

  const entityFields = STATIC_ENTITY_CONFIG?.[selectedEntity] || {};

  const handleDownloadCsv = () => {
    try {
      const csvHeaders = Object.entries(customizationData || {})
        .filter(
          ([key, value]) =>
            value?.visibility &&
            !removeFields.includes(key) &&
            (
              (!ADDITIONAL_ATTRIBUTE_FIELDS.includes(key) &&
                !CUSTOMIZABLE_FIELDS.includes(key)
              ) ||
              value?.type === ATTRIBUTE_TYPE_TEXT)
        )
        .map(([key, value]) => {
          const fieldConfig = entityFields[key];

          const label = fieldConfig?.label || key;

          const translatedLabel = handleValidateCodevalue(
            internationalise,
            label,
          );

          return `${translatedLabel} [${key}${value?.mandatory ? "*" : ""} ]`;
        });

      const csvContent = `${csvHeaders.join(",")}\n`;
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const fileUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = fileUrl;
      link.download = "sample-upload-template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(fileUrl);
    } catch (error) {
    }
  };

  const handleSelectFile = (event) => {
    try {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      if (
        file.type !== "text/csv" &&
        !file.name.toLowerCase().endsWith(".csv")
      ) {
        enqueueSnackbar(
          handleValidateCodevalue(internationalise, "COMMON_SELECT_CSV_WARNING"),
          {
            variant: "warning",
          },
        );
        fileInputRef.current.value = null;
      setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
  }
    catch (error) {

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }

    setSelectedFile(null);
  }
};

  useEffect(() => {
    getAllBulkUploadData();
  }, []);

  const getAllBulkUploadData = async () => {
    dispatch(requestShowLoader());
    const url = `${process.env.REACT_APP_BASE_URL}lead-bulk-uploads`;

    try {
      const response = await getRequestData(url);
      setDataGridData(response?.data);
    } catch {
    } finally {
      dispatch(requestHideLoader());
    }
  };

  const handleSaveData = async () => {
    try {
      if (!selectedFile) {
        enqueueSnackbar(
          handleValidateCodevalue(
            internationalise,
            "COMMON_SELECT_CSV_WARNING",
          ),
          {
            variant: "warning",
          },
        );

        return;
      }
      const formData = new FormData();

      formData.append("file", selectedFile);

      const url = `lead-bulk-uploads`;

      const response = await createRequestData(formData, url, true);

      if (response?.status >= 200 && response?.status < 300) {
        enqueueSnackbar(
          handleValidateCodevalue(
            internationalise,
            "FILE_IS_BEING_PROCESSED"), {
          variant: "success",
        });
        fileInputRef.current.value = null;
        setSelectedFile(null);
        await getAllBulkUploadData();
      } else {
        enqueueSnackbar(
          handleValidateCodevalue(
            internationalise,
            response?.propererror?.data?.message || "COMMON_PLEASE_TRY_AGAIN",
          ),
          {
            variant: "error",
          },
        );
      }
    } catch (error) {
      enqueueSnackbar(
        handleValidateCodevalue(
          internationalise,
          error?.propererror?.data?.message || "COMMON_PLEASE_TRY_AGAIN",
        ),
        {
          variant: "error",
        },
      );
    }
  };

  const handleDownloadDoc = async (documentId) => {
    await downloadDocument(documentId);
  };

  const columns = [
    {
      field: "documentId",
      headerName: handleValidateCodevalue(
        internationalise,
        "COMMON_BU_COL_DOC_ID",
      ),
      flex: 2,
      minWidth: workflowNameWidth,
    },
    {
      field: "totalRecords",
      headerName: handleValidateCodevalue(
        internationalise,
        "COMMON_BU_COL_TOTAL_RECORDS",
      ),
      flex: 1,
      minWidth: bulkUploadRecordsWidth,
    },
    {
      field: "totalSuccessRecords",
      headerName: handleValidateCodevalue(
        internationalise,
        "COMMON_BU_COL_SUCCESS_RECORDS",
      ),
      flex: 1,
      minWidth: bulkUploadRecordsWidth,
    },
    {
      field: "totalFailedRecords",
      headerName: handleValidateCodevalue(
        internationalise,
        "COMMON_BU_COL_FAILED_RECORDS",
      ),
      flex: 1,
      minWidth: bulkUploadRecordsWidth,
    },
    {
      field: "uploadedBy",
      headerName: handleValidateCodevalue(
        internationalise,
        "COMMON_BU_COL_UPLOADED_BY",
      ),
      flex: 1,
      valueGetter: ({ value }) => {
        const owner = ownerlist.find((c) => c.id === value);
        return owner
          ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim()
          : "";
      },
      minWidth: fullNameWidth,
    },
    {
      field: "uploadedAt",
      headerName: handleValidateCodevalue(
        internationalise,
        "COMMON_BU_COL_UPLOADED_AT",
      ),
      flex: 1.5,
      minWidth: whocolumnWidth,
      valueFormatter: (params) => {
        if (!params?.value) return "";
        return dateTimeISTFormatter(params?.value);
      },
    },
    {
      field: "processFinishTimestamp",
      headerName: handleValidateCodevalue(
        internationalise,
        "COMMON_BU_COL_PROCESSED_AT",
      ),
      flex: 1.5,
      minWidth: whocolumnWidth,
      valueFormatter: (params) => {
        if (!params?.value) return "";
        return dateTimeISTFormatter(params?.value);
      },
    },
    // {
    //   field: "createdAt",
    //   headerName: handleValidateCodevalue(
    //     internationalise,
    //     "COMMON_BU_COL_CREATED_AT",
    //   ),
    //   flex: 1.5,
    //   minWidth: whocolumnWidth,
    //   valueGetter: ({ value }) => value && moment(value).format("MM/DD/YYYY"),
    // },
    {
      field: "actions",
      type: "actions",
       renderHeader: () => (
        <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_ACTION')}>
          <DownloadIcon />
        </Tooltip>
      ),
      width: 100,
      cellClassName: "actions",
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={
              <Tooltip
                title={handleValidateCodevalue(
                  internationalise,
                  "COMMON_DOWNLOAD",
                )}
              >
                <DownloadIcon />
              </Tooltip>
            }
            className="textPrimary"
            disabled={!params?.row?.documentId}
            onClick={() => {
              handleDownloadDoc(params?.row?.documentId);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <BiztantuLoader open={false} isChildren={true}>
      <Box className={"globalBox"}>
        <StyledSubHeader>
          {handleValidateCodevalue(internationalise, "BULK_UPLOAD_AND_PROCESS")}
        </StyledSubHeader>

            <InfoIcon
                text = "BULK_UPLOAD_AND_PROCESS_INST"
            /> 
        <StyledLeadContainer>
          <StyledFlexStart>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleSelectFile}
            />

            {!!selectedFile && (
              <Tooltip
                title={handleValidateCodevalue(
                  internationalise,
                  "COMMON_UPLOAD",
                )}
              >
                <Button className="biztantu-sec-btn ml-20" onClick={handleSaveData}>
                  {handleValidateCodevalue(internationalise, "COMMON_UPLOAD")}
                </Button>
              </Tooltip>
            )}
          </StyledFlexStart>
          <Tooltip
            title={handleValidateCodevalue(internationalise, "COMMON_REFRESH")}
            onClick={getAllBulkUploadData}
          >
            <Button className="biztantu-sec-btn-mobile" variant="outlined">
              <RefreshIcon />
            </Button>
          </Tooltip>
        </StyledLeadContainer>
        <DataGridTable
          height = "calc(100vh - 320px)"
          isCheckboxReq={false}
          columns={columns}
          dataGridData={dataGridData}
        />
      </Box>
      <CreateLeadFooter>
        <Stack
          alignItems="center"
          display={"flex"}
          flexWrap={"wrap"}
          gap={"5px"}
          justifyContent={"space-between"}
          flexDirection="row"
        >
          <Typography variant="h6">
            {handleValidateCodevalue(internationalise, title)}
          </Typography>
          <>
            <Button
              className="edit-primary btn-space"
              variant="outlined"
              onClick={goBack}
            >
              {handleValidateCodevalue(internationalise, "COMMON_BACK")}
            </Button>
          </>
        </Stack>
        <Stack>
          <Tooltip
            title={handleValidateCodevalue(
              internationalise,
              "DOWNLOAD_SAMPLE_CSV",
            )}
          >
            <Button className="biztantu-sec-btn" onClick={handleDownloadCsv}>
              {handleValidateCodevalue(internationalise, "DOWNLOAD_SAMPLE_CSV")}
            </Button>
          </Tooltip>
        </Stack>
      </CreateLeadFooter>
    </BiztantuLoader>
  );
};

export default CommonBulkUpload;
